/**
 * Isometric line drawings for the webbyrå process (Kartläggning, Struktur och
 * design, Utveckling, Lansering och stöd). Same projection, frame, stroke and
 * animation hooks as the SEO drawings, with their own motifs and motion:
 * - 01 a magnifier slides over three audience cards that light up in turn
 * - 02 a wireframe on a drafting board fills in block by block
 * - 03 three layers (structure, design, code) settle into a stack
 * - 04 a desktop and a phone go live: a check is drawn, the phone pulses
 */

import type { CSSProperties } from "react";

import {
  box,
  CORNER_CROSSES,
  floorGrid,
  IllustrationFrame,
  line,
  motionPath,
  plane,
  polyline,
  project,
  type Point3,
  type ProcessIllustration,
  type Shape,
} from "@/components/services/seo/seo-process-illustrations";

/** A flat rectangle on a vertical face at depth y (spans x and z). */
function faceRect(x: number, y: number, z: number, w: number, h: number) {
  return polyline(
    [
      [x, y, z],
      [x + w, y, z],
      [x + w, y, z + h],
      [x, y, z + h],
    ],
    true,
  );
}

// ---------------------------------------------------------------- 01 Kartläggning

const CARD_XS = [10, 39, 68];
const CARD_Y = 40;
const CARD_FACE_Y = CARD_Y + 3;
/** Path for the magnifier along the card faces, ending at the last card. */
const LENS_PATH: Point3[] = [
  [14, CARD_FACE_Y, 24],
  [50, CARD_FACE_Y, 24],
  [79, CARD_FACE_Y, 24],
];

function DiscoveryIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 20, 0, 100, 60, 4),
    ...CARD_XS.flatMap((x) => [
      ...box(x, CARD_Y, 4, 22, 3, 30),
      // avatar and two text lines on each card face
      line([x + 6, CARD_FACE_Y, 26], [x + 16, CARD_FACE_Y, 26]),
      line([x + 4, CARD_FACE_Y, 16], [x + 18, CARD_FACE_Y, 16]),
      line([x + 4, CARD_FACE_Y, 11], [x + 14, CARD_FACE_Y, 11]),
    ]),
  ];

  const construct = [
    ...floorGrid(100, 20).filter((_, index) => index % 2 === 0),
    polyline([[0, CARD_FACE_Y + 12, 4], [100, CARD_FACE_Y + 12, 4]]),
  ];

  const [lensX, lensY] = project(LENS_PATH[LENS_PATH.length - 1]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      {CARD_XS.map((x, index) => {
        const [cx, cy] = project([x + 11, CARD_FACE_Y, 26]);
        return (
          <circle
            key={x}
            className="cc-ill-web-discovery__dot"
            style={{ "--cc-ill-node": index } as CSSProperties}
            cx={cx}
            cy={cy}
            r={3}
          />
        );
      })}
      <g className="cc-ill-web-discovery__lens" transform={`translate(${lensX.toFixed(1)} ${lensY.toFixed(1)})`}>
        <circle cx={0} cy={0} r={9} />
        <path d="M6.5 6.5l7 7" />
        <animateMotion
          begin="indefinite"
          dur="1.8s"
          fill="freeze"
          calcMode="spline"
          keyTimes="0;1"
          keySplines="0.45 0 0.25 1"
          path={motionPath(LENS_PATH)}
        />
      </g>
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 02 Struktur och design

const BOARD_FACE_Y = 30;
/** Wireframe blocks on the board face: header, hero, three cards. */
const WIREFRAME: { x: number; z: number; w: number; h: number }[] = [
  { x: 16, z: 56, w: 68, h: 5 },
  { x: 16, z: 32, w: 40, h: 18 },
  { x: 16, z: 14, w: 20, h: 12 },
  { x: 40, z: 14, w: 20, h: 12 },
  { x: 64, z: 14, w: 20, h: 12 },
];

function DesignIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 34, 0, 100, 50, 4),
    // drafting board standing on the plate
    ...box(10, 26, 4, 80, 4, 62),
    ...WIREFRAME.map((block) => ({ d: faceRect(block.x, BOARD_FACE_Y, block.z, block.w, block.h) })),
  ];

  const construct = [
    // column grid on the board
    ...[36, 50, 64].map((x) => polyline([[x, BOARD_FACE_Y, 10], [x, BOARD_FACE_Y, 62]])),
    polyline([[16, BOARD_FACE_Y, 30], [84, BOARD_FACE_Y, 30]]),
    polyline([[16, BOARD_FACE_Y, 52], [84, BOARD_FACE_Y, 52]]),
  ];

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      {WIREFRAME.map((block, index) => (
        <path
          key={index}
          className="cc-ill-web-design__block"
          style={{ "--cc-ill-segment": index } as CSSProperties}
          d={faceRect(block.x, BOARD_FACE_Y, block.z, block.w, block.h)}
        />
      ))}
      <circle className="cc-ill__dot" {...dotAt([60, BOARD_FACE_Y, 41])} r={2.6} />
    </IllustrationFrame>
  );
}

function dotAt(point: Point3) {
  const [cx, cy] = project(point);
  return { cx, cy };
}

// ---------------------------------------------------------------- 03 Utveckling

function DevelopmentIllustration({ maskId }: { maskId: string | null }) {
  const base: Shape[] = [...box(10, 10, 0, 80, 80, 4)];
  const layer = (z: number) => box(18, 18, z, 64, 64, 3);
  const codeZ = 41;

  const construct = [
    ...[
      [18, 82],
      [82, 82],
      [82, 18],
    ].map(([x, y]) => polyline([[x, y, 4], [x, y, 52]])),
    polyline([[10, 96, 0], [90, 96, 0]]),
  ];

  const codeLines = [
    polyline([[26, 30, codeZ + 3], [54, 30, codeZ + 3]]),
    polyline([[32, 40, codeZ + 3], [66, 40, codeZ + 3]]),
    polyline([[32, 50, codeZ + 3], [58, 50, codeZ + 3]]),
    polyline([[26, 60, codeZ + 3], [48, 60, codeZ + 3]]),
  ];

  return (
    <IllustrationFrame
      layers={[
        { shapes: base },
        { className: "cc-ill-web-dev__layer cc-ill-web-dev__layer--1", shapes: layer(10) },
        { className: "cc-ill-web-dev__layer cc-ill-web-dev__layer--2", shapes: layer(24) },
        { className: "cc-ill-web-dev__layer cc-ill-web-dev__layer--3", shapes: layer(38) },
      ]}
      construct={construct}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <g className="cc-ill-web-dev__code">
        {codeLines.map((d, index) => (
          <path key={index} d={d} pathLength={1} style={{ "--cc-ill-segment": index } as CSSProperties} />
        ))}
      </g>
      <circle className="cc-ill__dot" {...dotAt([74, 30, codeZ + 3])} r={2.6} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 04 Lansering och stöd

const SCREEN_FACE_Y = 43;
const CHECK: Point3[] = [
  [30, SCREEN_FACE_Y, 28],
  [37, SCREEN_FACE_Y, 22],
  [52, SCREEN_FACE_Y, 36],
];

function LaunchIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 20, 0, 100, 70, 4),
    // desktop: stand, foot and screen
    ...box(34, 46, 4, 16, 10, 2),
    ...box(40, 50, 6, 4, 2, 10),
    ...box(12, 40, 16, 60, 3, 36),
    { d: faceRect(16, SCREEN_FACE_Y, 20, 52, 28) },
    // phone lying beside it, screen up
    ...box(76, 54, 4, 14, 24, 2),
    { d: plane(78, 57, 6, 10, 17) },
  ];

  const construct = [
    polyline([[12, SCREEN_FACE_Y + 14, 4], [90, SCREEN_FACE_Y + 14, 4]]),
    polyline([[83, 66, 6], [83, 66, 30]]),
    polyline([[72, SCREEN_FACE_Y, 52], [90, SCREEN_FACE_Y, 52]]),
  ];

  const phone = project([83, 66, 6]);

  return (
    <IllustrationFrame layers={[{ shapes }]} construct={construct} crosses={CORNER_CROSSES} maskId={maskId}>
      <path className="cc-ill-web-launch__check" d={polyline(CHECK)} pathLength={1} />
      <circle className="cc-ill-web-launch__pulse" cx={phone[0]} cy={phone[1]} r={3} />
      <circle
        className="cc-ill-web-launch__pulse cc-ill-web-launch__pulse--late"
        cx={phone[0]}
        cy={phone[1]}
        r={3}
      />
      <circle className="cc-ill__dot" cx={phone[0]} cy={phone[1]} r={2.6} />
    </IllustrationFrame>
  );
}

export const WEB_PROCESS_ILLUSTRATIONS: ProcessIllustration[] = [
  DiscoveryIllustration,
  DesignIllustration,
  DevelopmentIllustration,
  LaunchIllustration,
];
